/**
 * AIQuizBuilder.tsx
 * AI-powered quiz generation from the current slide's text content.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, RefreshCw, Check, AlertCircle, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';
import Button from '@components/shared/Button';
import { useAppStore } from '@store/appStore';
import { extractSlideText } from '@office/slideService';
import { saveSlideConfig } from '@office/settingsService';
import { injectActivityShape, buildShapeDescriptor, getCurrentSlideIndex } from '@office/slideService';
import { generateQuizQuestion } from '@ai/geminiClient';
import { BLOOMS_LEVELS, getBloomsInfo } from '@utils/bloomsTaxonomy';
import { useEntitlements } from '@utils/entitlements';
import UpgradeGate from '@components/shared/UpgradeGate';
import type { AIGeneratedQuestion, BloomsLevel, MCConfig } from '@types-local/quiz.types';

export default function AIQuizBuilder(): React.ReactElement {
  const { ai } = useEntitlements();
  const [targetBlooms, setTargetBlooms] = useState<BloomsLevel | null>(null);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<AIGeneratedQuestion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showBlooms, setShowBlooms] = useState(false);
  const isOfficeReady = useAppStore((s) => s.isOfficeReady);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setResult(null);
    try {
      const aiSlideIndex = await getCurrentSlideIndex();
      const slideContent = await extractSlideText(aiSlideIndex);
      const question = await generateQuizQuestion(slideContent, targetBlooms);
      setResult(question);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setGenerating(false);
    }
  };

  const handleAddToSlide = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const config: MCConfig = {
        type: 'multiple_choice',
        question: result.question,
        options: result.options.map((text, i) => ({
          id: `ai_opt_${i}`,
          text,
          isCorrect: i === result.correctIndex,
        })),
        timeLimit: 30,
        competitionMode: false,
        starValue: 1,
        shuffleOptions: false,
        showCorrectAnswerAfter: true,
        blooms: result.blooms,
      };
      const slideIndex = await getCurrentSlideIndex();
      const shapeName = `IQ_AI_MC_${slideIndex.toString().padStart(2, '0')}`;
      await saveSlideConfig(slideIndex, config, shapeName);
      await injectActivityShape(buildShapeDescriptor(config, shapeName), slideIndex);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (!ai) {
    return <UpgradeGate feature="AI Quiz Builder" requiredTier="premium" />;
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-pink-500/20 border border-pink-500/30 flex items-center justify-center">
          <Sparkles size={16} className="text-pink-400" />
        </div>
        <div>
          <h2 className="text-sm font-bold text-slate-900">AI Quiz Builder</h2>
          <p className="text-[10px] text-slate-500">Gemini reads your slide and generates questions</p>
        </div>
      </div>

      {/* How it works */}
      <div className="rounded-xl bg-surface-elevated border border-surface-border p-3 space-y-1.5">
        {['1. Open the slide you want to quiz', '2. Choose a Bloom\'s level (optional)', '3. Click Generate — AI reads your slide text', '4. Review and add to slide'].map((step, i) => (
          <p key={i} className="text-xs text-slate-600">{step}</p>
        ))}
      </div>

      {/* Bloom's selector */}
      <div>
        <button
          onClick={() => setShowBlooms((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-surface-elevated border border-surface-border rounded-xl text-sm transition-colors hover:border-pink-500/30"
        >
          <span className="text-xs text-slate-600">
            {targetBlooms ? (
              <span className="text-slate-900 font-medium">{targetBlooms} Level</span>
            ) : (
              'Bloom\'s Level (optional)'
            )}
          </span>
          <ChevronDown size={14} className={clsx('text-slate-500 transition-transform', showBlooms && 'rotate-180')} />
        </button>

        <AnimatePresence>
          {showBlooms && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-2 gap-1.5 pt-2">
                {[null, ...BLOOMS_LEVELS].map((level) => {
                  const info = level ? getBloomsInfo(level) : null;
                  const isActive = targetBlooms === level;
                  return (
                    <button
                      key={level ?? 'any'}
                      onClick={() => { setTargetBlooms(level); setShowBlooms(false); }}
                      className={clsx(
                        'px-2.5 py-2 rounded-lg text-xs font-medium text-left transition-all',
                        isActive
                          ? 'bg-pink-500/20 border border-pink-500/40 text-pink-200'
                          : 'bg-surface-elevated border border-surface-border text-slate-600 hover:text-slate-900',
                      )}
                    >
                      {level ? `${info!.level}` : '🎲 Any Level'}
                      {level && <span className="block text-[10px] opacity-60 mt-0.5 truncate">{info!.description}</span>}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Generate button */}
      <Button fullWidth variant="primary" onClick={handleGenerate} loading={generating}
        disabled={!isOfficeReady}
        icon={<Sparkles size={14} />}
        className="bg-gradient-to-r from-pink-600 to-brand-500 hover:from-pink-700 hover:to-brand-600">
        {generating ? 'Reading slide…' : 'Generate Question'}
      </Button>

      {/* Error */}
      <AnimatePresence>
        {error && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="flex items-start gap-2.5 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2.5">
            <AlertCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-300 leading-relaxed">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Result */}
      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
            <div className="rounded-xl bg-surface-elevated border border-pink-500/20 p-3 space-y-3">
              {/* Blooms badge */}
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${getBloomsInfo(result.blooms).color} text-slate-900`}>
                  {result.blooms}
                </span>
                <span className="text-[10px] text-slate-500">AI Generated</span>
                <button onClick={handleGenerate} className="ml-auto text-slate-500 hover:text-pink-400 transition-colors">
                  <RefreshCw size={12} />
                </button>
              </div>

              {/* Question */}
              <p className="text-sm font-semibold text-slate-900 leading-snug">{result.question}</p>

              {/* Options */}
              <div className="space-y-1.5">
                {result.options.map((opt, i) => (
                  <div key={i}
                    className={clsx('flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs',
                      i === result.correctIndex
                        ? 'bg-green-500/15 border border-green-500/30 text-green-200'
                        : 'bg-surface border border-surface-border text-slate-600')}>
                    <span className="font-bold w-4 flex-shrink-0">
                      {i === result.correctIndex ? '✓' : ['A', 'B', 'C', 'D'][i]}
                    </span>
                    {opt}
                  </div>
                ))}
              </div>

              {/* Explanation */}
              {result.explanation && (
                <p className="text-[11px] text-slate-500 italic leading-relaxed border-t border-surface-border pt-2">
                  💡 {result.explanation}
                </p>
              )}
            </div>

            <Button fullWidth onClick={handleAddToSlide} loading={saving}
              variant={saved ? 'secondary' : 'primary'}
              icon={saved ? <Check size={14} /> : undefined}>
              {saved ? 'Added to Slide!' : 'Add to Slide'}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
